import { Button, Input } from "@win-reality/react-ui";
import { useEffect, useRef, useState } from "react";

import { supabaseClient } from "@/config/supabase";

const ResetPasswordFlowSupabase = () => {
  const [step, setStep] = useState<"email" | "code" | "password" | "success">(
    "email",
  );
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState<string[]>([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs: (HTMLInputElement | null)[] = [];
  const resendIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const code = otpDigits.join("");

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, []);

  const startResendCooldown = () => {
    // Clear any existing interval first
    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }

    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          resendIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    resendIntervalRef.current = interval;
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: signInError } = await supabaseClient.auth.signInWithOtp({
        email: email,
        options: {
          // Unique override string to indicate this is OTP in our email hook edge function, not an actual redirect url
          emailRedirectTo: "http://localhost/override/use-token",
        },
      });

      if (signInError) {
        console.error(
          "[ResetPasswordFlow] Sign in with OTP error:",
          signInError,
        );
        setError(signInError.message || "Failed to send OTP code");
        setIsLoading(false);
        return;
      }

      setStep("code");
      startResendCooldown();
    } catch (err) {
      console.error("[ResetPasswordFlow] Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setResendSuccess(false);
    setIsLoading(true);

    try {
      const { error: signInError } = await supabaseClient.auth.signInWithOtp({
        email: email,
        options: {
          // Unique override string to indicate this is OTP in our email hook edge function, not an actual redirect url
          emailRedirectTo: "http://localhost/override/use-token",
        },
      });

      if (signInError) {
        console.error("[ResetPasswordFlow] Resend OTP error:", signInError);
        setError(signInError.message || "Failed to resend OTP code");
        setIsLoading(false);
        return;
      }

      setOtpDigits(["", "", "", "", "", ""]);
      setResendSuccess(true);
      startResendCooldown();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("[ResetPasswordFlow] Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (code.length !== 6) {
        setError("Please enter all 6 digits");
        setIsLoading(false);
        return;
      }

      console.log("[ResetPasswordFlow] Verifying OTP code");
      const { error: verifyError } = await supabaseClient.auth.verifyOtp({
        email: email,
        token: code,
        type: "recovery",
      });

      if (verifyError) {
        console.error(
          "[ResetPasswordFlow] OTP verification error:",
          verifyError,
        );
        setError(verifyError.message || "Invalid or expired code");
        setIsLoading(false);
        return;
      }

      console.log("[ResetPasswordFlow] OTP verified successfully");
      setStep("password");
    } catch (err) {
      console.error("[ResetPasswordFlow] Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validate passwords
      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters");
        setIsLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError("Passwords don't match");
        setIsLoading(false);
        return;
      }

      console.log("[ResetPasswordFlow] Updating password");
      const { error: updateError } = await supabaseClient.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error(
          "[ResetPasswordFlow] Password update error:",
          updateError,
        );
        setError(updateError.message || "Failed to update password");
        setIsLoading(false);
        return;
      }

      console.log("[ResetPasswordFlow] Password updated successfully");
      setStep("success");
    } catch (err) {
      console.error("[ResetPasswordFlow] Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);

    // Auto-focus next field
    if (value && index < 5) {
      otpRefs[index + 1]?.focus();
    }
  };

  const handleOtpBackspace = (index: number) => {
    if (!otpDigits[index] && index > 0) {
      otpRefs[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6).split("");

    const newDigits = [...otpDigits];
    digits.forEach((digit, i) => {
      if (i < 6) newDigits[i] = digit;
    });
    setOtpDigits(newDigits);

    // Focus last filled or next empty field
    const nextEmpty = newDigits.findIndex((d) => !d);
    if (nextEmpty !== -1) {
      otpRefs[nextEmpty]?.focus();
    } else {
      otpRefs[5]?.focus();
    }
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordsValid =
    newPassword.length >= 8 && newPassword === confirmPassword;

  return (
    <form
      onSubmit={(e) => {
        if (step === "email") handleEmailSubmit(e);
        else if (step === "code") handleCodeSubmit(e);
        else if (step === "password") handlePasswordSubmit(e);
      }}
      className="w-full space-y-4"
    >
      {/* Email Step */}
      {step === "email" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Enter your email address and we'll send you a code to reset your
            password.
          </p>
          <div>
            <label htmlFor="reset-email" className="block text-sm mb-2">
              Email
            </label>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              required
              isDisabled={isLoading}
            />
          </div>
          {error && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}
          <Button
            type="submit"
            variant="secondary-flat-light"
            isDisabled={isLoading || !isEmailValid}
            isLoading={isLoading}
            className="w-full"
          >
            Send Code
          </Button>
        </div>
      )}

      {/* Code Step */}
      {step === "code" && (
        <div className="space-y-4 text-center">
          <p className="text-sm text-gray-600">
            A 6-digit code was sent to{" "}
            <span className="text-black font-bold">{email}</span>.
            <br />
            Enter it below.
          </p>
          <div className="flex gap-2 justify-center">
            {[0, 1, 2, 3, 4, 5].map((idx) => (
              <input
                key={idx}
                ref={(el) => {
                  if (el) otpRefs[idx] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                pattern="[0-9]*"
                value={otpDigits[idx]}
                onChange={(e) => handleOtpInput(idx, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Backspace") handleOtpBackspace(idx);
                }}
                onPaste={handleOtpPaste}
                autoComplete="one-time-code"
                className="w-12 h-12 text-center border border-gray-300 rounded-md text-xl focus:border-blue-500 focus:outline-none transition"
              />
            ))}
          </div>
          {resendSuccess && (
            <div className="w-full p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
              New code sent to <span className="font-bold">{email}</span>
            </div>
          )}
          {error && (
            <div className="w-full p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between gap-4">
              <div>{error}</div>
              {resendCooldown === 0 && (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                  className="text-black font-semibold hover:underline hover:opacity-70 disabled:opacity-50 whitespace-nowrap"
                >
                  Resend code
                </button>
              )}
            </div>
          )}
          <Button
            type="submit"
            variant="secondary-flat-light"
            isDisabled={isLoading || code.length !== 6}
            isLoading={isLoading}
            className="w-full"
          >
            Verify Code
          </Button>
        </div>
      )}

      {/* Password Step */}
      {step === "password" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Enter your new password.</p>
          <div>
            <label htmlFor="new-password" className="block text-sm mb-2">
              New Password
            </label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              required
              isDisabled={isLoading}
            />
            <p className="text-xs text-gray-600 mt-2">
              Must be at least 8 characters
            </p>
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm mb-2">
              Confirm Password
            </label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              required
              isDisabled={isLoading}
            />
          </div>

          {error && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="secondary-flat-light"
            isDisabled={isLoading || !isPasswordsValid}
            isLoading={isLoading}
            className="w-full"
          >
            Reset Password
          </Button>
        </div>
      )}

      {/* Success Step */}
      {step === "success" && (
        <div className="space-y-4 text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Password Reset Successful!
          </h3>
          <p className="text-sm text-gray-600">
            You can now log in with your new password.
          </p>
        </div>
      )}
    </form>
  );
};

export default ResetPasswordFlowSupabase;
